import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRoute,
  createRoute,
  createRouter,
  RouterProvider,
  redirect,
} from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ApiError, keys, serverNoteStore, undoReceipt, useSession } from "./api";
import { Shell } from "./components/Shell";
import { stopFocusZoom } from "./ios";
import { LayoutProvider } from "./layout/LayoutProvider";
import { type ActionRunners, NotifyProvider } from "./notify";
import { BorrowingPage } from "./pages/Borrowing";
import { BrewPage } from "./pages/Brews";
import { CountSheetPage } from "./pages/CountSheet";
import { InventoryPage } from "./pages/Inventory";
import { InvoiceReviewRoute } from "./pages/InvoiceReview";
import { InvoicesPage } from "./pages/Invoices";
import { ItemPage } from "./pages/Item";
import { LocationsPage } from "./pages/Locations";
import { MovementsPage } from "./pages/Movements";
import { NotificationsPage } from "./pages/Notifications";
import { ProductionPage } from "./pages/Production";
import { RecipePage } from "./pages/Recipe";
import { RecipeBuilderPage, RecipeEditPage } from "./pages/RecipeBuilder";
import { RecipesPage } from "./pages/Recipes";
import { SettingsPage } from "./pages/Settings";
import { SignInPage } from "./pages/SignIn";
import { StockImportPage } from "./pages/StockImport";
import { ToOrderPage } from "./pages/ToOrder";
import "./styles.css";

const rootRoute = createRootRoute({ component: Shell });
const routeTree = rootRoute.addChildren([
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/",
    component: InventoryPage,
    // What Inventory's search holds, so Back from an item comes back to the same matches.
    validateSearch: (search: Record<string, unknown>): { q?: string } =>
      typeof search.q === "string" && search.q ? { q: search.q } : {},
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/items/$itemId", component: ItemPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/count",
    component: CountSheetPage,
    // The location being counted, so a reload or a shared link opens the same sheet.
    validateSearch: (search: Record<string, unknown>): { location?: string } =>
      typeof search.location === "string" ? { location: search.location } : {},
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/invoices", component: InvoicesPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/invoices/$invoiceId",
    component: InvoiceReviewRoute,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/to-order", component: ToOrderPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/movements", component: MovementsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/borrowing", component: BorrowingPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/locations", component: LocationsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/settings", component: SettingsPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/settings/ollie/$importId",
    component: StockImportPage,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/recipes", component: RecipesPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/recipes/builder",
    component: RecipeBuilderPage,
    // The draft being written, once there is one: reloading picks it up. Copy on a recipe's page opens
    // it from that recipe.
    validateSearch: (search: Record<string, unknown>): { draft?: string; from?: string } => ({
      ...(typeof search.draft === "string" ? { draft: search.draft } : {}),
      ...(typeof search.from === "string" ? { from: search.from } : {}),
    }),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/recipes/$recipeId",
    component: RecipePage,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/recipes/$recipeId/edit",
    component: RecipeEditPage,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/production", component: ProductionPage }),
  // The Calendar and the Brew day list left the sidebar for Production (Scott's Jam, 2026-10-08), so a
  // bookmark to either opens Production.
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/calendar",
    beforeLoad: () => {
      throw redirect({ to: "/production", replace: true });
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/brews",
    beforeLoad: () => {
      throw redirect({ to: "/production", replace: true });
    },
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/brews/$brewId", component: BrewPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/notifications",
    component: NotificationsPage,
  }),
]);

const router = createRouter({ routeTree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// A request refused for want of a session (expired cookie, password changed) shows the sign-in page.
function onSignedOut(error: unknown) {
  if (error instanceof ApiError && error.status === 401 && error.code === "signed_out") {
    queryClient.setQueryData(keys.session, { required: true, signedIn: false });
  }
}

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onSignedOut }),
  mutationCache: new MutationCache({ onError: onSignedOut }),
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      // A 4xx will not fix itself; anything else might be a dropped connection on the floor.
      retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 3,
    },
  },
});

/** Notifications are kept on the server, so a reload or another device still has them. */
const noteStore = serverNoteStore();

/**
 * What a kept notification's action does, by kind. Kept as data so it can be pressed from the bell or
 * the Notifications page long after the screen that raised it has gone.
 */
const noteActions: ActionRunners = {
  undo_receipt: {
    label: "Undo",
    running: "Undoing",
    done: "Undone",
    run: async (action) => {
      await undoReceipt(action.invoiceId);
      await queryClient.invalidateQueries();
    },
    after: {
      level: "notice",
      title: "Receipt undone",
      body: "Everything this invoice put on the shelf has been taken back off it. Both entries stay in the log.",
    },
  },
};

function App() {
  const session = useSession();
  if (session.isLoading) return null;
  if (session.data?.required && !session.data.signedIn) return <SignInPage />;
  return <RouterProvider router={router} />;
}

stopFocusZoom();

const root = document.getElementById("root");
if (root) {
  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <LayoutProvider>
          <NotifyProvider store={noteStore} runners={noteActions}>
            <App />
          </NotifyProvider>
        </LayoutProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}
